import React, { CSSProperties } from "react";
import { Event } from "@api/routers/events";
import { eventStatusMap } from "@utils/constants";
import { useParams } from "react-router-dom";
import { useEditorStore } from "@state/editor";
import { useThemeStore } from "@state/theme";
import { useConfigStore } from "@state/config";
import { getCardWidth } from "@utils/sizingUtils";

interface EventCardProps {
  event: Event;
  isPortrait?: boolean;
}

interface CustomCSSProperties extends CSSProperties {
  "--focus-ring-color"?: string;
}

const formatRuntime = (totalMinutes: number): string => {
  const hours = Math.floor(totalMinutes / 60);
  const mins  = totalMinutes % 60;

  if (hours === 0) {
    return `${mins}m`;
  }

  if (mins === 0) {
    return `${hours}h`;
  }

  return `${hours}h ${String(mins).padStart(2, "0")}m`;
};
const EventCard: React.FC<EventCardProps> = ({ event, isPortrait }) => {
  console.log(event)
  const description = event.description?.replace(/<\/?[^>]+(>|$)/g, "");
  const renderPanelWidth = useEditorStore(({ renderPanelSize }) => renderPanelSize.width);
  const focusRingColor = useThemeStore(({ focusRingColor }) => focusRingColor);
  const { thumbMissingUrl } = useConfigStore();
  const params = useParams();
  const { appID } = params;

  const status = eventStatusMap[event.eventStatus];
  
  const isPublished = event.eventStatus === "published";
  const hasRuntime  = !!event.estRuntime;
  const showChip = !isPublished || (isPublished && hasRuntime);

  const cardWidth = getCardWidth(renderPanelWidth || 0, isPortrait);

  return (
    <div
      key={event.eventID}
      className={`event-card flex flex-col mr-4 my-1 group hover:scale-[1.01] focus:scale-[1.01] transition-transform duration-75
         rounded-lg md:rounded-xl outline-none focus:ring-2 focus:ring-[var(--focus-ring-color)] hover:ring-2 hover:ring-[var(--focus-ring-color)]`}
      style={
        {
          "--focus-ring-color": focusRingColor,
        } as CustomCSSProperties
      }
    >
      <div
        className={`relative w-full flex flex-col justify-between transition-all duration-300 rounded-lg md:rounded-xl`}
        style={{
          width: cardWidth,
        }}
      >
        <img
          src={event.videoThumbnail ? event.videoThumbnail : thumbMissingUrl}
          alt="Event Thumbnail"
          className={`rounded-lg md:rounded-xl ${isPortrait ? "aspect-portrait object-cover" : "aspect-video"}`}
        />

        {/* Gradient from left to right black to transparent if portrait*/}
        {isPortrait && <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-lg md:rounded-xl"></div>}

        {showChip && (
          <div
            className="px-1 py-0.5 rounded-md text-[10px] absolute font-semibold uppercase top-2 right-2 text-white"
            style={{ backgroundColor: status?.hex }}
          >
            {isPublished && hasRuntime
              ? formatRuntime(event.estRuntime!)   // 1h 07m, etc.
              : status?.label}
          </div>
        )}

        <div className={`rounded-b-lg md:rounded-b-xl ${isPortrait ? "absolute bottom-0 left-0 right-0 pl-3 pb-3" : "pl-1 pb-1"}`}>
          <p className={`font-bold text-white pt-0.5 truncate ${!isPortrait && "whitespace-normal line-clamp-1"} group-hover:whitespace-normal group-focus:whitespace-normal`}>{event.title}</p>

          <p className="text-xs font-semibold text-white">
            {new Date(event.startDateTime).toLocaleDateString("en-US", {
              month: "2-digit",
              day: "2-digit",
              year: "numeric",
            })}
          </p>
        </div>
      </div>
    </div>
  );
};

export default EventCard;
